/**
 * Proposal detail — view a proposal and advance its status (owner/office; gated server-side).
 *
 * Reached by tapping a proposal row on the Government hub. Refetches the proposals list (real GET
 * round-trip) and finds this proposal by id, then a status change issues the real
 * PATCH /api/gov/proposals/:id/status and invalidates the list so the hub reflects the new status.
 *
 * LEGAL / SAFETY: reference & tracking only — BEAM never submits a proposal. Changing status only
 * records the state the human chose. NEVER imports @smartbox/* (mobile invariant 1). No raw styles.
 */
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listProposals,
  updateProposalStatus,
  type ProposalRow,
  type ProposalStatus,
} from '../../../../src/api/gov';
import { Screen, Text, Card, Button, Select } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';
import { extractErrorMessage, humanize } from '../../../../src/lib/format';

const STATUSES: { value: ProposalStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'internal_review', label: 'Internal review' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

export function ProposalDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const queryClient = useQueryClient();

  const proposalsQuery = useQuery({ queryKey: ['gov-proposals'], queryFn: listProposals, staleTime: 30_000 });

  const statusMut = useMutation({
    mutationFn: (status: ProposalStatus) => updateProposalStatus(id!, status),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['gov-proposals'] }),
  });

  if (proposalsQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading proposal" />
        </View>
      </Screen>
    );
  }

  if (proposalsQuery.isError) {
    return (
      <Screen>
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(proposalsQuery.error, 'Failed to load the proposal.')}
          </Text>
          <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => proposalsQuery.refetch()} />
        </Card>
      </Screen>
    );
  }

  const proposal = (proposalsQuery.data ?? []).find((p: ProposalRow) => p.id === id);
  if (!proposal) {
    return (
      <Screen>
        <Card>
          <Text variant="body" color="secondary">Proposal not found.</Text>
          <Button title="Back to government" variant="secondary" onPress={() => router.replace('/(app)/government')} />
        </Card>
      </Screen>
    );
  }

  return (
    <Screen scroll>
      <Text variant="title">Proposal v{proposal.version}</Text>
      <Card>
        <Text variant="small" color="secondary">
          {proposal.submitted_at ? `Submitted ${new Date(proposal.submitted_at).toLocaleDateString()}` : 'Not submitted'}
        </Text>
        <Text variant="small" color="secondary">Current status: {humanize(proposal.status)}</Text>
      </Card>

      <Text variant="label" color="secondary" style={styles.fieldLabel}>Update status</Text>
      <Select
        options={STATUSES}
        value={proposal.status as ProposalStatus}
        onChange={(s) => statusMut.mutate(s)}
      />
      {statusMut.isPending ? (
        <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Updating status" />
      ) : null}
      {statusMut.isError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(statusMut.error, 'Could not update the proposal status.')}
          </Text>
        </Card>
      ) : null}

      <Button title="Back to government" variant="secondary" onPress={() => router.replace('/(app)/government')} />
    </Screen>
  );
}

const styles = StyleSheet.create({
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: theme.spacing.md },
  fieldLabel: { marginTop: theme.spacing.sm, marginBottom: theme.spacing.xs },
});
